import { downtimeReasonIdentity } from "../../../shared/shift-production-form/downtime-reason-identity";
import type { ShiftFormManagerDashboard, ShiftFormManagerDashboardFilters } from "../../../shared/shift-production-form/contracts";

export const analysisNumber = (value: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(value);
export const analysisMinutes = (value: number | null) => value === null ? "Süre eksik" : `${analysisNumber(value)} dk`;
export const analysisShiftTimes = { "00_08": "00:00–08:00", "08_16": "08:00–16:00", "16_00": "16:00–00:00" };
export function drillIntoMachine(filters: ShiftFormManagerDashboardFilters, lineName: NonNullable<ShiftFormManagerDashboardFilters["lineName"]>) {
  return { ...filters, lineName, shift: undefined };
}
export function drillIntoShift(filters: ShiftFormManagerDashboardFilters, shift: NonNullable<ShiftFormManagerDashboardFilters["shift"]>) {
  return { ...filters, shift };
}
export function clearAnalysisDrilldown(filters: ShiftFormManagerDashboardFilters) {
  return { ...filters, lineName: undefined, shift: undefined };
}

export type AnalysisSourceFilter =
  | { kind: "DOWNTIME" }
  | { kind: "REASON_KEY"; reasonKey: string; label: string }
  | { kind: "REASON"; reason: string; department: string }
  | { kind: "WASTE"; key: string; unit: string; label: string }
  | { kind: "OUTPUT"; unit: string };
export function analysisSourceFilterLabel(filter: AnalysisSourceFilter) {
  if (filter.kind === "DOWNTIME") return "Bildirilen duruş";
  if (filter.kind === "REASON_KEY") return filter.label;
  if (filter.kind === "REASON") return `${filter.reason || "Nedeni belirtilmemiş"} · ${filter.department || "Birim belirtilmemiş"}`;
  if (filter.kind === "WASTE") return `${filter.label} · ${filter.unit}`;
  return `Üretim · ${filter.unit}`;
}
export function matchingAnalysisSources(rows: ShiftFormManagerDashboard["rows"], filter: AnalysisSourceFilter | null) {
  if (!filter) return rows;
  return rows.filter(row => {
    if (filter.kind === "DOWNTIME") return true;
    if (filter.kind === "REASON_KEY") return row.downtimes.some(item => downtimeReasonIdentity(item.reason).key === filter.reasonKey);
    if (filter.kind === "REASON") return row.downtimes.some(item => item.reason === filter.reason && item.responsibleDepartment === filter.department);
    if (filter.kind === "WASTE") return row.wasteEntries.some(item => item.key === filter.key && item.unit === filter.unit);
    return row.actualOutputs.some(item => item.unit === filter.unit);
  });
}

export function analysisSourceMeasure(row: ShiftFormManagerDashboard["rows"][number], filter: AnalysisSourceFilter | null) {
  if (filter?.kind === "DOWNTIME") return row.downtimeMinutes === null ? "Ölçüm eksik" : `${analysisNumber(row.downtimeMinutes)} dk`;
  if (filter?.kind === "REASON" || filter?.kind === "REASON_KEY") {
    const durations = row.downtimes.filter(item => filter.kind === "REASON_KEY" ? downtimeReasonIdentity(item.reason).key === filter.reasonKey : item.reason === filter.reason && item.responsibleDepartment === filter.department).map(item => item.durationMinutes);
    if (!durations.length) return "Ölçüm eksik";
    return analysisMinutes(durations.some(value => value === null) ? null : durations.reduce<number>((sum, value) => sum + (value ?? 0), 0));
  }
  const values = filter?.kind === "WASTE"
    ? row.wasteEntries.filter(item => item.key === filter.key && item.unit === filter.unit)
    : row.actualOutputs.filter(item => !filter || item.unit === filter.unit);
  return values.map(item => `${analysisNumber(item.value)} ${item.unit}`).join(" · ") || "Ölçüm eksik";
}
